import { useMemo } from "react";

import { formatPointsSimple } from "@vericus/cadmus-common";
import {
  ArrowDownTrayIcon,
  EyeIcon,
  QuestionRoundIcon,
} from "@vericus/cadmus-icons";
import { Button, Pill, Text, Tooltip, vars } from "@vericus/cadmus-ui";

import clsx from "clsx";
import { format } from "date-fns";

import squigglyNoteIcon from "@/assets/illustrations/squiggly-note.svg";
import { FeedbackTabId } from "@/features/feedback/components/types";
import { formatDecimalNumber } from "@/features/feedback/utils";
import { useMarkingToolLaunch } from "@/features/submission";
import {
  FeedbackReleaseType,
  FeedbackVisibilityFragment,
  MarkingTool,
  PeerEvaluationMode,
  SubmissionType,
  useSimilarityQuery,
} from "@/generated/graphql";
import { useFeatureFlagsFragment } from "@/graphql/institution-selectors";
import { useDownloadFeedbackPdf } from "@/hooks/use-download-feedback-pdf";

import {
  FeedbackItemContent,
  getVisibilityConstraints,
} from "../utils/feedback-visibility";
import * as styles from "./feedback-item.css";

interface FeedbackItemProps {
  id: FeedbackItemContent;
  title: string;
  /* When feedbackVisibility is null, the WorkOutcomeRelease
  does not exist for the component. */
  feedbackVisibility: FeedbackVisibilityFragment | null;
  weight: number | null;
  maxGrade?: number | null;
  finalScore?: number | null;
  submissionType?: SubmissionType;
  workId: string;
  tenant: string;
  releaseDate?: string | null;
  // the tab to go to in the GroupFeedback Page.
  feedbackTab: FeedbackTabId;
  peerEvaluationMode?: PeerEvaluationMode | null;
  groupReportScaleFactor?: number | null;
}

const FALLBACK_VISIBILITY_CONSTRAINTS = {
  showMarks: false,
  showFeedbackCta: false,
  showGrades: false,
  disableAccess: false,
  canDownLoadPdf: false,
} as const;

function feedbackStatus(
  submissionType: SubmissionType | undefined,
  id: FeedbackItemContent,
  releaseType?: FeedbackReleaseType
) {
  if (submissionType === SubmissionType.Final) {
    // Regardless of type of release, Self eval is always a final release with the text "Completed"
    if (id === FeedbackItemContent.Self)
      return {
        content: "Completed",
        bg: vars.colors.successBg,
        color: "#145252",
      };

    // For final release
    // In the case of Peer eval, it's always considered a final release
    if (
      releaseType === FeedbackReleaseType.Final ||
      id === FeedbackItemContent.Peer
    )
      return {
        content: "Feedback released",
        bg: vars.colors.successBg,
        color: "#145252",
      };

    if (releaseType === FeedbackReleaseType.Provisional)
      return {
        content: "Provisional feedback",
        bg: vars.colors.bgrey300,
        color: "#3B404F",
        provisionalTooltip: true,
      };
  }

  if (submissionType === SubmissionType.Draft) {
    return {
      content: "Draft feedback released",
      bg: vars.colors.linkBg,
      color: vars.colors.navy500,
    };
  }

  return {
    content: "Pending release",
    bg: vars.colors.yellow200,
    color: vars.colors.yellow800,
  };
}

export function FeedbackItem({
  id,
  title,
  weight,
  feedbackVisibility,
  feedbackTab,
  submissionType,
  finalScore,
  maxGrade,
  workId,
  tenant,
  releaseDate,
  peerEvaluationMode,
  groupReportScaleFactor,
}: FeedbackItemProps) {
  const {
    showMarks,
    showFeedbackCta,
    showGrades,
    disableAccess,
    canDownLoadPdf,
  } = !feedbackVisibility
    ? {
        ...FALLBACK_VISIBILITY_CONSTRAINTS,
        showFeedbackCta: submissionType === SubmissionType.Draft,
      }
    : getVisibilityConstraints(id, feedbackVisibility);

  const features = useFeatureFlagsFragment();
  const isTurnitinEnabled = features?.turnitin === true;

  const downloadPdf = useDownloadFeedbackPdf(workId ?? "", tenant);
  const isDownloadable =
    (feedbackTab == FeedbackTabId.GroupReport ||
      feedbackTab == FeedbackTabId.IndividualReport) &&
    canDownLoadPdf;

  // The parent component uses submissionType as a flag
  // for if a WorkOutcome has a release.
  const isReady = useMemo(() => {
    return submissionType != null;
  }, [submissionType]);

  // Skip query if component is not individual | group,
  // or has no release.
  const { data } = useSimilarityQuery({
    variables: {
      workId: workId,
      submissionType: submissionType ?? SubmissionType.Final,
    },
    skip:
      !(
        feedbackTab == FeedbackTabId.GroupReport ||
        feedbackTab == FeedbackTabId.IndividualReport
      ) || !isReady,
  });
  const similarity = data?.latestResult.similarity;

  const status = feedbackStatus(
    submissionType,
    id,
    feedbackVisibility?.releaseType
  );

  const getScore = (score?: number | null) => {
    if (score === null || score === undefined) return "--";
    return formatPointsSimple(score);
  };

  const isScalingMode = peerEvaluationMode === PeerEvaluationMode.Scaling;
  const isPeerScaling = isScalingMode && id === FeedbackItemContent.Peer;
  const isSelfScaling = isScalingMode && id === FeedbackItemContent.Self;
  const renderMarks = showMarks && !isSelfScaling;
  const renderCta = showFeedbackCta && !isSelfScaling;

  // trackGradebookAccess set to false to track access
  // within GroupFeedbackPage. use useGraderLaunch for
  // redirect only.
  const resultLaunch = useMarkingToolLaunch({
    workId: workId,
    markingTool: MarkingTool.Cadmusgrader,
    submissionType: submissionType ?? SubmissionType.Final,
    isReady,
    component: feedbackTab,
    trackGradebookAccess: false,
  });
  return (
    <div className={styles.feedbackItem}>
      <div className={styles.feedbackTitle}>
        <Text kind="headingFour">{title}</Text>
        {!!similarity && isTurnitinEnabled && (
          <Text kind="label">{similarity.display}% similarity score</Text>
        )}
        <div className={styles.feedbackStatus}>
          <Pill basic color={status.bg} textColor={status.color}>
            {status.content}
          </Pill>
          {status.provisionalTooltip === true && (
            <Tooltip
              content={
                <Text kind="bodyLine">
                  This is NOT your final grade. The
                  <br />
                  final feedback will be released
                  <br />
                  separately by your instructor.
                </Text>
              }
              side="bottom"
              width="auto"
            >
              <QuestionRoundIcon label="info" size={20} />
            </Tooltip>
          )}
          {releaseDate != null && (
            <Text kind="bodySm">
              {format(new Date(releaseDate), "dd MMM yyyy")}
            </Text>
          )}
        </div>
      </div>
      <div className={styles.feedbackItemOptions}>
        {renderMarks && (
          <div className={clsx(styles.gradeContent, styles.feedbackGrade)}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                width: "100%",
              }}
            >
              <img src={squigglyNoteIcon} width={39} height={32} alt="" />
              <div>
                {isPeerScaling ? (
                  <>
                    <Text kind="headingFour">
                      {groupReportScaleFactor === null ||
                      groupReportScaleFactor === undefined
                        ? "--"
                        : formatDecimalNumber(groupReportScaleFactor)}
                    </Text>
                    <Text kind="label">Score multiplier</Text>
                  </>
                ) : (
                  <>
                    <Text kind="headingFour">
                      {weight === 0
                        ? "-- / --"
                        : showGrades
                          ? `${getScore(finalScore)}/${getScore(maxGrade)}`
                          : `xx/${getScore(maxGrade)}`}
                    </Text>
                    <Text kind="label">{weight ?? 0}% weighting</Text>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <div className={styles.feedbackActions}>
          {renderCta && (
            <Button
              disabled={!isReady || disableAccess}
              kind="secondary"
              onClick={resultLaunch}
              icon={<EyeIcon size={24} label="" />}
            >
              View feedback
            </Button>
          )}
          {isDownloadable && (
            <Button
              style={{ width: "fit-content" }}
              icon={<ArrowDownTrayIcon size={24} label="" />}
              kind="outline"
              onClick={downloadPdf}
              aria-label="Download Feedback PDF"
              disabled={
                submissionType !== SubmissionType.Final || disableAccess
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}
